const CONSISTENCY_TOKEN_STORAGE_KEY = 'graphqlConsistencyTokens';
const MAX_STORED_CONSISTENCY_TOKENS = 5;
const CONSISTENCY_TOKEN_COOKIE_NAME = 'resqConsistencyTokenBridge';

export const CONSISTENCY_TOKEN_HEADER = 'Resq-Consistency-Token';
export const CONSISTENCY_SATISFIED_HEADER = 'Resq-Consistency-Satisfied';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type CookieAdapter = {
    get: () => string;
    set: (value: string) => void;
};

const getSessionStorage = (): StorageLike | null => {
    try {
        return window.sessionStorage ?? null;
    } catch {
        return null;
    }
};

const getCookieAdapter = (): CookieAdapter | null => {
    try {
        return {
            get: () => document.cookie ?? '',
            set: (value) => {
                document.cookie = value;
            },
        };
    } catch {
        return null;
    }
};

const getCookieValue = (name: string, cookie = getCookieAdapter()): string | null => {
    if (!cookie) {
        return null;
    }
    const prefix = `${name}=`;
    for (const entry of cookie.get().split(';')) {
        const trimmedEntry = entry.trim();
        if (trimmedEntry.startsWith(prefix)) {
            return trimmedEntry.slice(prefix.length);
        }
    }
    return null;
};

const getCookieDeletionDomains = (): string[] => {
    const rootDomain =
        typeof window === 'undefined'
            ? ''
            : ((window as typeof window & { ENV?: { ROOT_DOMAIN?: string } }).ENV?.ROOT_DOMAIN ??
              '');
    const normalizedRootDomain = rootDomain.trim().toLowerCase().replace(/^\.+/, '');
    if (!normalizedRootDomain || ['localhost', '127.0.0.1', '::1'].includes(normalizedRootDomain)) {
        return [];
    }

    return [normalizedRootDomain, `.${normalizedRootDomain}`];
};

const clearCookie = (name: string, cookie = getCookieAdapter()): void => {
    if (!cookie) {
        return;
    }

    cookie.set(`${name}=; Max-Age=0; path=/`);
    for (const domain of getCookieDeletionDomains()) {
        cookie.set(`${name}=; Max-Age=0; path=/; domain=${domain}`);
    }
};

const readStoredTokens = (storage: StorageLike | null): string[] => {
    if (!storage) {
        return [];
    }

    const rawValue = storage.getItem(CONSISTENCY_TOKEN_STORAGE_KEY);
    if (!rawValue) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        if (!Array.isArray(parsedValue)) {
            return [];
        }
        return parsedValue.filter(
            (token): token is string => typeof token === 'string' && token.length > 0,
        );
    } catch {
        return [];
    }
};

const writeStoredTokens = (tokens: string[], storage: StorageLike | null): void => {
    if (!storage) {
        return;
    }

    if (tokens.length === 0) {
        storage.removeItem(CONSISTENCY_TOKEN_STORAGE_KEY);
        return;
    }

    storage.setItem(CONSISTENCY_TOKEN_STORAGE_KEY, JSON.stringify(tokens));
};

export const getConsistencyTokenHeaderValue = (
    storage = getSessionStorage(),
    cookie = getCookieAdapter(),
): string | null => {
    const tokens = readStoredTokens(storage);
    if (tokens.length > 0) {
        return tokens.join(', ');
    }

    const cookieToken = getCookieValue(CONSISTENCY_TOKEN_COOKIE_NAME, cookie);
    if (!cookieToken) {
        return null;
    }

    if (!storage) {
        return cookieToken;
    }

    storeConsistencyToken(cookieToken, storage);
    clearCookie(CONSISTENCY_TOKEN_COOKIE_NAME, cookie);
    return readStoredTokens(storage).join(', ');
};

export const storeConsistencyToken = (
    token: string | null | undefined,
    storage = getSessionStorage(),
): void => {
    const trimmedToken = token?.trim();
    if (!trimmedToken) {
        return;
    }

    const existingTokens = readStoredTokens(storage).filter((value) => value !== trimmedToken);
    writeStoredTokens(
        [...existingTokens, trimmedToken].slice(-MAX_STORED_CONSISTENCY_TOKENS),
        storage,
    );
};

export const clearConsistencyTokens = (storage = getSessionStorage()): void => {
    if (!storage) {
        return;
    }

    storage.removeItem(CONSISTENCY_TOKEN_STORAGE_KEY);
};

export const syncConsistencyTokensFromHeaders = (
    headers: Headers,
    storage = getSessionStorage(),
    cookie = getCookieAdapter(),
): void => {
    const consistencyToken = headers.get(CONSISTENCY_TOKEN_HEADER);
    if (consistencyToken) {
        storeConsistencyToken(consistencyToken, storage);
        return;
    }

    if (headers.get(CONSISTENCY_SATISFIED_HEADER) === '1') {
        clearConsistencyTokens(storage);
        clearCookie(CONSISTENCY_TOKEN_COOKIE_NAME, cookie);
    }
};
